import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Star, Lock, CheckCircle2, Rocket, Crown, CalendarDays, Clock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import TopBar from "../components/TopBar";

export default function StudyPlanDetail() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const t = useT();
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api.get(`/plans/${planId}`).then((r) => { setPlan(r.data.plan); if (r.data.user) setUser(r.data.user); }).catch(() => {});
    // eslint-disable-next-line
  }, [planId]);

  if (!plan) return <div className="space-bg min-h-screen flex items-center justify-center text-white/60">{t("Caricamento...")}</div>;

  return (
    <div className="space-bg min-h-screen pb-20">
      <TopBar />
      <div className="max-w-lg mx-auto px-4 pt-5">
        <button onClick={() => navigate("/plans")} className="flex items-center gap-1 text-white/60 bouncy mb-4" data-testid="plandetail-back-btn">
          <ChevronLeft size={18} /> {t("I miei piani")}
        </button>
        <h1 className="font-heading font-black text-2xl tracking-tight mb-1">{plan.title}</h1>
        <p className="text-white/60 text-sm mb-4">{plan.summary}</p>
        <div className="flex gap-2 text-xs font-num mb-6">
          <span className="glass rounded-full px-3 py-1 flex items-center gap-1"><CalendarDays size={12} /> {plan.days} {t("giorni")}</span>
          <span className="glass rounded-full px-3 py-1 flex items-center gap-1"><Clock size={12} /> {plan.hours_per_day}h/g</span>
        </div>

        {plan.schedule?.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading font-bold text-lg tracking-tight mb-3">{t("📅 Programma")}</h2>
            <div className="space-y-2">
              {plan.schedule.map((d, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                  className="glass rounded-2xl p-4" data-testid={`schedule-day-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-heading font-bold" style={{ color: "var(--cyan)" }}>{t("Giorno")} {d.day}</span>
                    <span className="text-xs text-white/50 font-num">{d.hours}h</span>
                  </div>
                  <p className="text-sm font-semibold">{d.focus}</p>
                  {d.theory?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-heading font-bold" style={{ color: "var(--cyan)" }}>{t("📖 Teoria da studiare")}</p>
                      <ul className="mt-1 space-y-1">
                        {d.theory.map((tt, ti) => (
                          <li key={ti} className="text-xs text-white/60 flex gap-2"><span style={{ color: "var(--cyan)" }}>▸</span>{tt}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {d.exercises?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-heading font-bold" style={{ color: "var(--gold)" }}>{t("✏️ Esercizi da fare")}</p>
                      <ul className="mt-1 space-y-1">
                        {d.exercises.map((tt, ti) => (
                          <li key={ti} className="text-xs text-white/60 flex gap-2"><span style={{ color: "var(--gold)" }}>▸</span>{tt}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {d.tasks?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {d.tasks.map((tt, ti) => (
                        <li key={ti} className="text-xs text-white/60 flex gap-2"><span style={{ color: "var(--violet)" }}>▸</span>{tt}</li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <h2 className="font-heading font-bold text-lg tracking-tight mb-4">{t("🎯 Livelli & Sfide")}</h2>
        <div className="relative flex flex-col items-center gap-10">
          {plan.levels.map((lv, i) => {
            const side = i % 2 === 0 ? "-translate-x-16" : "translate-x-16";
            const locked = !lv.unlocked;
            return (
              <div key={lv.level_id} className={`relative ${side}`} data-testid={`plan-node-${lv.level_id}`}>
                {i > 0 && <div className="absolute left-1/2 -top-10 h-10 w-1 -translate-x-1/2" style={{ background: locked ? "rgba(255,255,255,0.15)" : "var(--violet)", borderRadius: 4 }} />}
                <motion.button whileHover={locked ? {} : { scale: 1.1 }} whileTap={locked ? {} : { scale: 0.92 }} disabled={locked}
                  onClick={() => navigate(`/quiz/${lv.level_id}`)}
                  className="relative w-20 h-20 rounded-full flex items-center justify-center font-num font-bold text-xl"
                  style={{
                    background: locked ? "rgba(31,40,51,0.6)" : lv.is_boss ? "radial-gradient(circle at 30% 30%, #FFD700, #b8860b)" : "radial-gradient(circle at 30% 30%, #7d75ff, #6C63FF)",
                    boxShadow: locked ? "none" : `0 0 22px ${lv.is_boss ? "#FFD700aa" : "#6C63FFaa"}`,
                    border: lv.completed ? "3px solid var(--success)" : "3px solid rgba(255,255,255,0.25)", opacity: locked ? 0.55 : 1,
                  }} data-testid={`plan-level-btn-${lv.level_id}`}>
                  {locked ? <Lock size={24} className="text-white/60" /> : lv.is_boss ? <Crown size={26} className="text-white" /> : lv.index}
                  {!locked && <div className="absolute -bottom-6 flex gap-0.5">
                    {[1, 2, 3].map((s) => <Star key={s} size={14} className={s <= lv.stars ? "fill-current" : ""} style={{ color: s <= lv.stars ? "var(--gold)" : "rgba(255,255,255,0.25)" }} />)}
                  </div>}
                </motion.button>
                <div className="absolute top-1/2 -translate-y-1/2 left-24 w-40 text-sm text-white/70 hidden sm:block">{lv.topic}</div>
              </div>
            );
          })}
          <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 2.5 }} className="mt-2">
            <Rocket size={28} style={{ color: "var(--violet)" }} />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
